import { z } from "zod";
import {
  SharedApiError,
  normalizeApiBaseUrl,
  parseErrorResponse,
  type FetchLike,
} from "./shared";

export const toggleChannelMuteBodySchema = z.object({
  muted: z.boolean(),
});

export const channelMembershipWriteSuccessSchema = z.object({
  ok: z.literal(true),
});

async function parseChannelMembershipWriteSuccess(
  response: Response,
  fallbackMessage: string,
): Promise<z.infer<typeof channelMembershipWriteSuccessSchema>> {
  const payload =
    typeof response.json === "function"
      ? await response.json().catch(() => null)
      : null;
  const parsed = channelMembershipWriteSuccessSchema.safeParse(payload);
  if (!parsed.success) {
    throw new SharedApiError(fallbackMessage, 500, {
      issues: parsed.error.issues,
    });
  }
  return parsed.data;
}

export function joinChannelPath(channelId: string): string {
  return `/api/channels/${encodeURIComponent(channelId)}/join`;
}

export function leaveChannelPath(channelId: string): string {
  return `/api/channels/${encodeURIComponent(channelId)}/leave`;
}

export function toggleChannelMutePath(channelId: string): string {
  return `/api/channels/${encodeURIComponent(channelId)}/mute`;
}

export function markChannelReadPath(channelId: string): string {
  return `/api/channels/${encodeURIComponent(channelId)}/read`;
}

export async function joinChannelRequest({
  apiBaseUrl,
  accessToken,
  channelId,
  fetchImpl,
}: {
  apiBaseUrl: string;
  accessToken: string;
  channelId: string;
  fetchImpl?: FetchLike;
}): Promise<z.infer<typeof channelMembershipWriteSuccessSchema>> {
  const fetchFn = fetchImpl ?? fetch;
  const response = await fetchFn(
    `${normalizeApiBaseUrl(apiBaseUrl)}${joinChannelPath(channelId)}`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    },
  );

  if (!response.ok) {
    throw await parseErrorResponse(response, "Failed to join channel");
  }

  return parseChannelMembershipWriteSuccess(
    response,
    "Invalid join channel response",
  );
}

export async function leaveChannelRequest({
  apiBaseUrl,
  accessToken,
  channelId,
  fetchImpl,
}: {
  apiBaseUrl: string;
  accessToken: string;
  channelId: string;
  fetchImpl?: FetchLike;
}): Promise<z.infer<typeof channelMembershipWriteSuccessSchema>> {
  const fetchFn = fetchImpl ?? fetch;
  const response = await fetchFn(
    `${normalizeApiBaseUrl(apiBaseUrl)}${leaveChannelPath(channelId)}`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    },
  );

  if (!response.ok) {
    throw await parseErrorResponse(response, "Failed to leave channel");
  }

  return parseChannelMembershipWriteSuccess(
    response,
    "Invalid leave channel response",
  );
}

export async function toggleChannelMuteRequest({
  apiBaseUrl,
  accessToken,
  channelId,
  body,
  fetchImpl,
}: {
  apiBaseUrl: string;
  accessToken: string;
  channelId: string;
  body: z.input<typeof toggleChannelMuteBodySchema>;
  fetchImpl?: FetchLike;
}): Promise<z.infer<typeof channelMembershipWriteSuccessSchema>> {
  const fetchFn = fetchImpl ?? fetch;
  const parsedBody = toggleChannelMuteBodySchema.parse(body);
  const response = await fetchFn(
    `${normalizeApiBaseUrl(apiBaseUrl)}${toggleChannelMutePath(channelId)}`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(parsedBody),
    },
  );

  if (!response.ok) {
    throw await parseErrorResponse(
      response,
      "Failed to update channel mute state",
    );
  }

  return parseChannelMembershipWriteSuccess(
    response,
    "Invalid channel mute response",
  );
}

export async function markChannelReadRequest({
  apiBaseUrl,
  accessToken,
  channelId,
  fetchImpl,
}: {
  apiBaseUrl: string;
  accessToken: string;
  channelId: string;
  fetchImpl?: FetchLike;
}): Promise<z.infer<typeof channelMembershipWriteSuccessSchema>> {
  const fetchFn = fetchImpl ?? fetch;
  const response = await fetchFn(
    `${normalizeApiBaseUrl(apiBaseUrl)}${markChannelReadPath(channelId)}`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    },
  );

  if (!response.ok) {
    throw await parseErrorResponse(response, "Failed to mark channel as read");
  }

  return parseChannelMembershipWriteSuccess(
    response,
    "Invalid mark channel read response",
  );
}

// --- Admin add channel member ---

export const addChannelMemberBodySchema = z.object({
  user_id: z.string(),
});

export function addChannelMemberPath(channelId: string): string {
  return `/api/channels/${encodeURIComponent(channelId)}/members`;
}

export async function addChannelMemberRequest({
  apiBaseUrl,
  accessToken,
  channelId,
  userId,
  fetchImpl,
}: {
  apiBaseUrl: string;
  accessToken: string;
  channelId: string;
  userId: string;
  fetchImpl?: FetchLike;
}): Promise<z.infer<typeof channelMembershipWriteSuccessSchema>> {
  const fetchFn = fetchImpl ?? fetch;
  const parsedBody = addChannelMemberBodySchema.parse({ user_id: userId });
  const response = await fetchFn(
    `${normalizeApiBaseUrl(apiBaseUrl)}${addChannelMemberPath(channelId)}`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(parsedBody),
    },
  );

  if (!response.ok) {
    throw await parseErrorResponse(response, "Failed to add channel member");
  }

  return parseChannelMembershipWriteSuccess(
    response,
    "Invalid add channel member response",
  );
}
